import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SupportStyleTable from "../../../Components/Table/SupportStyleTable";
import { fetchMembers } from "../../../Redux/Slice/Homepage";
import "../../CustomerReq/InhouseRnd.css";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Briefcase,
  Eye,
  X,
} from "lucide-react";

const MemberAvatar = ({ photo, name, size = "w-8 h-8" }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  const getInitials = (n) => {
    if (!n) return "EM";
    const parts = n.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  };

  if (photo && String(photo).trim() !== "" && !hasError) {
    return (
      <img
        src={photo}
        alt={name || "Member"}
        className={`${size} rounded-full object-cover shrink-0 border border-slate-200 shadow-xs`}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full shrink-0 border border-slate-200 bg-blue-100 text-[#112A47] flex items-center justify-center text-[11px] font-bold tracking-tight uppercase shadow-xs`}
    >
      {getInitials(name)}
    </div>
  );
};

const ProfileModal = ({ isOpen, onClose, MemberData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#133A69] to-[#0d284a] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MemberAvatar
              photo={MemberData?.ProfilePhoto}
              name={MemberData?.name}
              size="w-10 h-10"
            />
            <div>
              <h3 className="text-base font-bold text-white leading-tight">
                {MemberData?.name || "Member Profile"}
              </h3>
              <p className="text-xs text-slate-300">
                {MemberData?.position || "Team Member"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5">
          {MemberData ? (
            <div className="space-y-3.5">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Briefcase size={16} className="text-[#133A69] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Designation / Role</span>
                  <span className="text-sm font-semibold text-slate-800">{MemberData.position || "--"}</span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Mail size={16} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Email Address</span>
                  <span className="text-sm font-medium text-slate-700 break-words">{MemberData.Email || "--"}</span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <Phone size={16} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Mobile Number</span>
                  <span className="text-sm font-medium text-slate-700">{MemberData.mobileNumber || "--"}</span>
                </div>
              </div>

              {MemberData.Address && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <MapPin size={16} className="text-slate-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">Address</span>
                    <span className="text-sm font-medium text-slate-700">{MemberData.Address}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Account Status</span>
                {MemberData.status === "On" ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                    Inactive
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-slate-500 text-sm py-4">No profile data available.</p>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold bg-[#133A69] hover:bg-[#0d284a] text-white rounded-lg shadow-xs transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const HomeShowAllMembers = () => {
  const dispatch = useDispatch();
  const { membersData } = useSelector((state) => state.Home);

  const [activeButton, setActiveButton] = useState("Active Member");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    dispatch(fetchMembers());
  }, [dispatch]);

  const handleViewProfile = (profileData) => {
    setSelectedProfile(profileData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProfile(null);
  };

  const rows = useMemo(() => {
    return Array.isArray(membersData?.data)
      ? membersData.data.map((el, index) => ({
          id: el.id || index + 1,
          MainId: el._id,
          mobileNumber: el.MobileNumber,
          name: el.Name,
          Email: el.Email,
          Password: el.Password,
          position: el.Role,
          Address: el.Address,
          ProfilePhoto: el.ProfilePhoto,
          JoiningDate: el.JoiningDate,
          status: el.Profile,
        }))
      : [];
  }, [membersData]);

  const totalCount = rows.length;
  const activeCount = rows.filter((row) => row.status === "On").length;
  const nonActiveCount = rows.filter((row) => row.status === "Off").length;

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (activeButton === "All Member") return true;
      if (activeButton === "Active Member") return row.status === "On";
      if (activeButton === "Non Active Member") return row.status === "Off";
      return true;
    });
  }, [rows, activeButton]);

  // Reusable cell renderers
  const renderIdCell = (params) => (
    <span className="inline-flex items-center justify-center font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
      #{params.value}
    </span>
  );

  const renderNameCell = (params) => (
    <div className="flex items-center gap-2.5 min-w-0">
      <MemberAvatar
        photo={params.row.ProfilePhoto}
        name={params.value}
        size="w-7 h-7"
      />
      <div className="flex flex-col min-w-0">
        <span className="font-semibold text-slate-800 truncate" title={params.value}>
          {params.value || "--"}
        </span>
        {params.row.position && (
          <span className="text-[11px] text-slate-500 font-medium truncate">
            {params.row.position}
          </span>
        )}
      </div>
    </div>
  );

  const renderMobileCell = (params) => (
    <div className="inline-flex items-center gap-1.5 text-slate-600 font-medium text-xs">
      <Phone size={12} className="text-slate-400 shrink-0" />
      <span>{params.value || "--"}</span>
    </div>
  );

  const renderStatusCell = (params) => {
    const isOnline = params.value === "On";
    return isOnline ? (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Active
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
        Inactive
      </span>
    );
  };

  const columns = [
    { field: "id", headerName: "ID", width: 65, minWidth: 60, renderCell: renderIdCell },
    { field: "name", headerName: "MEMBER NAME", minWidth: 240, renderCell: renderNameCell },
    { field: "mobileNumber", headerName: "MOBILE NUMBER", minWidth: 160, renderCell: renderMobileCell },
    { field: "status", headerName: "STATUS", width: 120, minWidth: 110, renderCell: renderStatusCell },
    {
      field: "viewProfile",
      headerName: "ACTION",
      width: 125,
      minWidth: 125,
      renderCell: (params) => (
        <button
          type="button"
          onClick={() => handleViewProfile(params.row)}
          className="action-btn action-btn-show shadow-xs"
          title="View Member Profile"
        >
          <Eye size={13} />
          <span>View Profile</span>
        </button>
      ),
    },
  ];

  return (
    <div className="inhouse-card">
      {/* Card Header */}
      <div className="inhouse-card-header" style={{ borderLeft: "4px solid #6366f1" }}>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-indigo-600">
            Team Members Directory
          </span>
          <span className="text-slate-500 text-xs font-medium">
            Directory of registered developers and support personnel
          </span>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
          Total: {totalCount}
        </span>
      </div>

      {/* Card Body */}
      <div className="inhouse-card-body space-y-3">
        {/* Status Tabs Bar */}
        <div className="dashboard-tabs-bar">
          <button
            type="button"
            className={`tab-btn-status tab-btn-queue ${activeButton === "All Member" ? "active" : ""}`}
            onClick={() => setActiveButton("All Member")}
          >
            <span className="tab-status-dot"></span>
            <span>All Member</span>
            <span className="tab-badge">{totalCount}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-complete ${activeButton === "Active Member" ? "active" : ""}`}
            onClick={() => setActiveButton("Active Member")}
          >
            <span className="tab-status-dot"></span>
            <span>Active Member</span>
            <span className="tab-badge">{activeCount}</span>
          </button>

          <button
            type="button"
            className={`tab-btn-status tab-btn-holding ${activeButton === "Non Active Member" ? "active" : ""}`}
            onClick={() => setActiveButton("Non Active Member")}
          >
            <span className="tab-status-dot"></span>
            <span>Non Active Member</span>
            <span className="tab-badge">{nonActiveCount}</span>
          </button>
        </div>

        {/* SupportStyleTable */}
        <SupportStyleTable
          tableId="home_all_members"
          fileName={`Members_${activeButton.replace(/\s+/g, "_")}`}
          columns={columns}
          rows={filteredRows}
          fullHeight={false}
          emptyComponent="No members found in this category."
        />
      </div>

      {/* Modern Profile Modal */}
      <ProfileModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        MemberData={selectedProfile}
      />
    </div>
  );
};

export default HomeShowAllMembers;
